import React, { useState, useEffect } from 'react';
import apiClient from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Users, BarChart3, TrendingUp, Wallet, ShieldCheck, Download, Trash2, MessageSquare, Mail, CreditCard, ExternalLink, Eye, CheckCircle2, History, Search, ChevronLeft, ChevronRight, ArrowUpDown, Calendar, Edit } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Label } from '../components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const AdminDashboard = () => {
  const { t, language } = useLanguage();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [comments, setComments] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [analyticsData, setAnalyticsData] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // User Edit State
  const [editingUser, setEditingUser] = useState(null); // { id, username, email, paypal_link }
  const [showUserEditModal, setShowUserEditModal] = useState(false);

  // Market Edit State
  const [editingMarket, setEditingMarket] = useState(null); // { id, title, end_date }
  const [showMarketEditModal, setShowMarketEditModal] = useState(false);

  const [editingBalance, setEditingBalance] = useState(null); // { id, balance }
  const [balanceValue, setBalanceValue] = useState('');

  // Search & Pagination States
  const [searchQuery, setSearchQuery] = useState({
    users: '',
    comments: '',
    transactions: '',
    markets: ''
  });
  const [currentPage, setCurrentPage] = useState({
    users: 1,
    comments: 1,
    transactions: 1,
    markets: 1
  });
  const [userSort, setUserSort] = useState({ field: 'created_at', direction: 'desc' });
  const recordsPerPage = 10;

  // Confirmation Modals State
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    title: '',
    description: '',
    onConfirm: () => {},
    variant: 'default'
  });

  // Resolve Modal State
  const [resolveModal, setResolveModal] = useState({
    open: false,
    marketId: null,
    marketTitle: ''
  });
  const [resolving, setResolving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, usersRes, commentsRes, marketsRes, categoriesRes, transactionsRes, analyticsRes] = await Promise.all([
        apiClient.get('/admin/stats'),
        apiClient.get('/admin/users'),
        apiClient.get('/admin/comments'),
        apiClient.get('/admin/markets'),
        apiClient.get('/admin/categories'),
        apiClient.get('/admin/transactions'),
        apiClient.get('/admin/stats/analytics')
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setComments(commentsRes.data);
      setMarkets(marketsRes.data);
      setCategories(categoriesRes.data);
      setTransactions(transactionsRes.data);
      setAnalyticsData(analyticsRes.data);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const startEditingBalance = (u) => {
    setEditingBalance(u.id);
    setBalanceValue(u.balance.toString());
  };

  const handleBalanceUpdate = async (id) => {
    try {
      await apiClient.patch(`/admin/users/${id}/balance`, { balance: parseFloat(balanceValue) });
      toast.success(language === 'de' ? 'Kontostand aktualisiert' : 'Balance updated');
      setEditingBalance(null);
      fetchData();
    } catch (e) {
      toast.error(language === 'de' ? 'Fehler beim Aktualisieren' : 'Update failed');
    }
  };

  const handleUserUpdate = async () => {
    try {
      await apiClient.patch(`/admin/users/${editingUser.id}`, {
        username: editingUser.username,
        email: editingUser.email,
        paypal_link: editingUser.paypal_link
      });
      toast.success(language === 'de' ? 'Benutzer aktualisiert' : 'User updated');
      setShowUserEditModal(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || (language === 'de' ? 'Fehler beim Aktualisieren' : 'Update failed'));
    }
  };

  const handleMarketEndDateUpdate = async () => {
    try {
      const [hours, minutes] = editingMarket.end_time.split(':').map(Number);
      const combinedDate = new Date(editingMarket.end_date);
      combinedDate.setHours(hours, minutes, 0, 0);

      await apiClient.patch(`/admin/markets/${editingMarket.id}/end-date`, {
        end_date: combinedDate.toISOString()
      });
      toast.success(language === 'de' ? 'Zeitpunkt aktualisiert' : 'End date/time updated');
      setEditingMarket(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || (language === 'de' ? 'Fehler beim Aktualisieren' : 'Update failed'));
    }
  };

  const deleteUser = async (id) => {
    setConfirmModal({
      open: true,
      title: language === 'de' ? 'Benutzer löschen?' : 'Delete User?',
      description: language === 'de' ? 'Möchtest du diesen Benutzer wirklich unwiderruflich löschen?' : 'Are you sure you want to permanently delete this user?',
      variant: 'destructive',
      onConfirm: async () => {
        try {
          await apiClient.delete(`/admin/users/${id}`);
          toast.success(language === 'de' ? 'Benutzer gelöscht' : 'User deleted');
          fetchData();
        } catch (e) {
          toast.error(language === 'de' ? 'Fehler beim Löschen' : 'Delete failed');
        }
      }
    });
  };

  const confirmUser = async (id) => {
    try {
      await apiClient.patch(`/admin/users/${id}/confirm`);
      toast.success(language === 'de' ? 'Benutzer bestätigt' : 'User confirmed');
      fetchData();
    } catch (e) {
      toast.error(language === 'de' ? 'Fehler beim Bestätigen' : 'Confirmation failed');
    }
  };

  const toggleAdmin = async (id) => {
    try {
      await apiClient.patch(`/admin/users/${id}/toggle-admin`);
      toast.success(language === 'de' ? 'Admin-Status aktualisiert' : 'Admin status updated');
      fetchData();
    } catch (e) {
      toast.error(language === 'de' ? 'Fehler beim Aktualisieren' : 'Update failed');
    }
  };

  const deleteMarket = async (id) => {
    setConfirmModal({
      open: true,
      title: language === 'de' ? 'Wette löschen?' : 'Delete Market?',
      description: language === 'de' ? 'Möchtest du diese Wette wirklich löschen?' : 'Are you sure you want to delete this market?',
      variant: 'destructive',
      onConfirm: async () => {
        try {
          await apiClient.delete(`/admin/markets/${id}`);
          toast.success(language === 'de' ? 'Wette gelöscht' : 'Market deleted');
          fetchData();
        } catch (e) {
          toast.error(language === 'de' ? 'Fehler beim Löschen' : 'Delete failed');
        }
      }
    });
  };

  const resolveMarketAdmin = async (id, winner) => {
    setResolving(true);
    try {
      await apiClient.post(`/markets/${id}/resolve`, { winner });
      toast.success(language === 'de' ? 'Wette erfolgreich abgeschlossen' : 'Market resolved successfully');
      setResolveModal({ open: false, marketId: null, marketTitle: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resolve market');
    } finally {
      setResolving(false);
    }
  };

  const deleteCategory = async (id) => {
    setConfirmModal({
      open: true,
      title: language === 'de' ? 'Kategorie löschen?' : 'Delete Category?',
      description: language === 'de' ? 'Möchtest du diese Kategorie wirklich löschen?' : 'Are you sure you want to delete this category?',
      variant: 'destructive',
      onConfirm: async () => {
        try {
          await apiClient.delete(`/admin/categories/${id}`);
          toast.success(language === 'de' ? 'Kategorie gelöscht' : 'Category deleted');
          fetchData();
        } catch (e) {
          toast.error(language === 'de' ? 'Fehler beim Löschen' : 'Delete failed');
        }
      }
    });
  };

  const deleteComment = async (id) => {
    setConfirmModal({
      open: true,
      title: language === 'de' ? 'Kommentar löschen?' : 'Delete Comment?',
      description: language === 'de' ? 'Möchtest du diesen Kommentar wirklich löschen?' : 'Are you sure you want to delete this comment?',
      variant: 'destructive',
      onConfirm: async () => {
        try {
          await apiClient.delete(`/admin/comments/${id}`);
          toast.success('Kommentar gelöscht');
          fetchData();
        } catch (e) {
          toast.error('Fehler beim Löschen');
        }
      }
    });
  };

  const reverseTransaction = async (id, description) => {
    setConfirmModal({
      open: true,
      title: language === 'de' ? 'Transaktion stornieren?' : 'Reverse Transaction?',
      description: language === 'de' 
        ? `Möchtest du die Transaktion "${description}" wirklich rückgängig machen? Der Betrag wird dem User-Konto gegengerechnet.` 
        : `Are you sure you want to reverse the transaction "${description}"? The amount will be offset on the user's account.`,
      onConfirm: async () => {
        try {
          await apiClient.post(`/admin/transactions/${id}/reverse`);
          toast.success(language === 'de' ? 'Transaktion storniert' : 'Transaction reversed');
          fetchData();
        } catch (e) {
          toast.error(e.response?.data?.detail || 'Fehler beim Stornieren');
        }
      }
    });
  };

  const exportUsers = () => {
    window.open('/api/admin/export/users', '_blank');
  };

  // Helper for Pagination & Filtering
  const getPaginatedData = (data, type, filterFn, sortFn = null) => {
    let filtered = data.filter(filterFn);
    if (sortFn) {
      filtered = [...filtered].sort(sortFn);
    }
    const totalPages = Math.ceil(filtered.length / recordsPerPage);
    const start = (currentPage[type] - 1) * recordsPerPage;
    const paginated = filtered.slice(start, start + recordsPerPage);
    return { data: paginated, totalPages, totalCount: filtered.length };
  };

  const handleUserSort = (field) => {
    setUserSort(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
    setCurrentPage(prev => ({ ...prev, users: 1 }));
  };

  const renderPagination = (type, totalPages) => {
    if (totalPages <= 1) return null;
    return (
      <div className="flex items-center justify-end gap-2 mt-4">
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => setCurrentPage(prev => ({ ...prev, [type]: Math.max(1, prev[type] - 1) }))}
          disabled={currentPage[type] === 1}
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <span className="text-xs font-medium">
          Seite {currentPage[type]} von {totalPages}
        </span>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => setCurrentPage(prev => ({ ...prev, [type]: Math.min(totalPages, prev[type] + 1) }))}
          disabled={currentPage[type] === totalPages}
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    );
  };

  const renderSearch = (type, placeholder) => (
    <div className="relative mb-4">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
      <Input
        placeholder={placeholder}
        value={searchQuery[type]}
        onChange={(e) => {
          setSearchQuery(prev => ({ ...prev, [type]: e.target.value }));
          setCurrentPage(prev => ({ ...prev, [type]: 1 }));
        }}
        className="pl-10 h-9 text-sm"
      />
    </div>
  );

  const LiquidityTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const val = payload[0].value;
      const d = new Date(label);
      const formattedDate = d.toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric' });
      return (
        <div className="bg-card/90 backdrop-blur-md border border-border rounded-xl p-3 shadow-2xl animate-in fade-in duration-200">
          <p className="text-xs text-muted-foreground font-sans font-medium mb-1">{formattedDate}</p>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-sans font-bold">
              {language === 'de' ? 'Gesamt-Ergebnis' : 'Total Net Result'}
            </p>
            <p className="text-base font-mono font-bold text-green-400">
              {val.toLocaleString(language === 'de' ? 'de-DE' : 'en-US')} €
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  const UserGrowthTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const val = payload[0].value;
      const d = new Date(label);
      const formattedDate = d.toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric' });
      return (
        <div className="bg-card/90 backdrop-blur-md border border-border rounded-xl p-3 shadow-2xl animate-in fade-in duration-200">
          <p className="text-xs text-muted-foreground font-sans font-medium mb-1">{formattedDate}</p>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-sans font-bold">
              {language === 'de' ? 'Neue User' : 'New Users'}
            </p>
            <p className="text-base font-mono font-bold text-blue-400">
              {val}
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="app-container">
        <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
          <Skeleton className="h-10 w-48" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24" />)}
          </div>
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-8 h-8 text-blue-500" />
            <h1 className="text-3xl font-bold">Admin Dashboard</h1>
          </div>
          <Button onClick={exportUsers} className="gap-2">
            <Download className="w-4 h-4" />
            Export Users (CSV)
          </Button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-blue-500/20 bg-blue-500/5">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="p-3 bg-blue-500/20 rounded-xl"><Users className="text-blue-500 w-5 h-5" /></div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Total Users</p>
                <p className="text-2xl font-bold">{stats?.total_users}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-red-500/20 bg-red-500/5">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="p-3 bg-red-500/20 rounded-xl"><BarChart3 className="text-red-500 w-5 h-5" /></div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Total Markets</p>
                <p className="text-2xl font-bold">{stats?.total_markets}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-green-500/20 bg-green-500/5">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="p-3 bg-green-500/20 rounded-xl"><TrendingUp className="text-green-500 w-5 h-5" /></div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Total Bets</p>
                <p className="text-2xl font-bold">{stats?.total_bets}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-yellow-500/20 bg-yellow-500/5">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="p-3 bg-yellow-500/20 rounded-xl"><Wallet className="text-yellow-500 w-5 h-5" /></div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">{language === 'de' ? 'Gesamt-Ergebnis' : 'Total Net Result'}</p>
                <p className="text-xl font-bold">{stats?.total_balance.toLocaleString()} €</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Platform Analytics Graphen */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg font-bold">
                <TrendingUp className="w-5 h-5 text-green-500" />
                {language === 'de' ? 'Gesamt-Ergebnis (30 Tage)' : 'Total Net Result (30 Days)'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px] w-full mt-2">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={analyticsData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorLiquidity" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#22c55e" stopOpacity={0.2}/>
                        <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis 
                      dataKey="date" 
                      stroke="hsl(var(--muted-foreground))"
                      fontSize={10} 
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(str) => {
                        const d = new Date(str);
                        return d.toLocaleDateString(undefined, {day: '2-digit', month: '2-digit'});
                      }}
                      style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                    />
                    <YAxis 
                      stroke="hsl(var(--muted-foreground))"
                      fontSize={10} 
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(val) => `${val.toLocaleString(language === 'de' ? 'de-DE' : 'en-US')} €`} 
                      style={{ fontFamily: 'JetBrains Mono, monospace' }}
                    />
                    <Tooltip content={<LiquidityTooltip />} />
                    <Area 
                      type="linear" 
                      dataKey="liquidity" 
                      stroke="#22c55e" 
                      strokeWidth={2} 
                      fill="url(#colorLiquidity)"
                      activeDot={{ r: 5, stroke: '#fff', strokeWidth: 1.5 }}
                      animationDuration={800}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg font-bold">
                <Users className="w-5 h-5 text-blue-500" />
                {language === 'de' ? 'Benutzer-Zuwachs (30 Tage)' : 'User Growth (30 Days)'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px] w-full mt-2">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={analyticsData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.2}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis 
                      dataKey="date" 
                      stroke="hsl(var(--muted-foreground))"
                      fontSize={10} 
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(str) => {
                        const d = new Date(str);
                        return d.toLocaleDateString(undefined, {day: '2-digit', month: '2-digit'});
                      }}
                      style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                    />
                    <YAxis 
                      stroke="hsl(var(--muted-foreground))"
                      fontSize={10} 
                      tickLine={false}
                      axisLine={false}
                      allowDecimals={false} 
                      style={{ fontFamily: 'JetBrains Mono, monospace' }}
                    />
                    <Tooltip content={<UserGrowthTooltip />} />
                    <Bar 
                      dataKey="new_users" 
                      fill="url(#colorUsers)" 
                      radius={[4, 4, 0, 0]} 
                      maxBarSize={30}
                      animationDuration={800}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* User Management volle Breite */}
        <Card className="w-full">
          <CardHeader className="pb-2">
            <CardTitle>User Management</CardTitle>
          </CardHeader>
          <CardContent>
            {renderSearch('users', 'Suchen nach Name oder E-Mail...')}
            <div className="overflow-x-auto min-h-[450px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead className="cursor-pointer hover:bg-secondary/50 transition-colors" onClick={() => handleUserSort('balance')}>
                      <div className="flex items-center gap-1">
                        {language === 'de' ? 'Gewinn / Verlust' : 'Profit / Loss'}
                        <ArrowUpDown className={cn("w-3 h-3", userSort.field === 'balance' ? "text-primary" : "text-muted-foreground/30")} />
                      </div>
                    </TableHead>
                    <TableHead className="cursor-pointer hover:bg-secondary/50 transition-colors" onClick={() => handleUserSort('created_at')}>
                      <div className="flex items-center gap-1">
                        Joined
                        <ArrowUpDown className={cn("w-3 h-3", userSort.field === 'created_at' ? "text-primary" : "text-muted-foreground/30")} />
                      </div>
                    </TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(() => {
                    const userSortFn = (a, b) => {
                      const factor = userSort.direction === 'asc' ? 1 : -1;
                      if (userSort.field === 'balance') return (a.balance - b.balance) * factor;
                      if (userSort.field === 'created_at') return (new Date(a.created_at) - new Date(b.created_at)) * factor;
                      return 0;
                    };

                    const { data } = getPaginatedData(users, 'users', u => 
                      u.username.toLowerCase().includes(searchQuery.users.toLowerCase()) ||
                      u.email.toLowerCase().includes(searchQuery.users.toLowerCase()),
                      userSortFn
                    );
                    return (
                      <>
                        {data.map(u => (
                          <TableRow key={u.id} className={!u.is_confirmed ? "bg-yellow-500/5" : ""}>
                            <TableCell>
                              <div className="flex flex-col">
                                <div className="flex items-center gap-2">
                                  <span className="font-bold text-sm">@{u.username}</span>
                                  <Button 
                                    variant="ghost" 
                                    size="icon" 
                                    className="h-6 w-6 text-muted-foreground hover:text-primary"
                                    onClick={() => {
                                      setEditingUser({ id: u.id, username: u.username, email: u.email, paypal_link: u.paypal_link || '' });
                                      setShowUserEditModal(true);
                                    }}
                                  >
                                    <Edit className="w-3 h-3" />
                                  </Button>
                                </div>
                                <Link to={`/user/${u.id}`} className="flex items-center gap-1 text-[10px] text-blue-400 hover:underline mt-1">
                                  <Eye className="w-3 h-3" />
                                  View Profile
                                </Link>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-col gap-1">
                                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                  <Mail className="w-3 h-3 shrink-0" />
                                  <span className="truncate max-w-[120px]" title={u.email}>{u.email}</span>
                                </div>
                                {u.paypal_link && (
                                  <a 
                                    href={u.paypal_link} 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    className="flex items-center gap-1.5 text-[10px] text-blue-400 hover:underline"
                                  >
                                    <CreditCard className="w-3 h-3 shrink-0" />
                                    PayPal
                                  </a>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              {editingBalance === u.id ? (
                                <div className="flex items-center gap-1">
                                  <Input 
                                    type="number" 
                                    value={balanceValue} 
                                    onChange={(e) => setBalanceValue(e.target.value)}
                                    className="h-8 w-20 text-xs px-2"
                                    autoFocus
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') handleBalanceUpdate(u.id);
                                      if (e.key === 'Escape') setEditingBalance(null);
                                    }}
                                  />
                                  <Button size="icon" className="h-8 w-8" onClick={() => handleBalanceUpdate(u.id)}>
                                    <ShieldCheck className="w-4 h-4" />
                                  </Button>
                                </div>
                              ) : (
                                <div 
                                  className={cn("text-sm font-mono cursor-pointer hover:bg-secondary/50 p-1 rounded transition-colors", u.balance < 0 ? 'text-red-400' : 'text-green-400')}
                                  onClick={() => startEditingBalance(u)}
                                  title="Click to edit"
                                >
                                  {u.balance.toLocaleString()} €
                                </div>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                <Calendar className="w-3 h-3 shrink-0" />
                                <span>{new Date(u.created_at).toLocaleDateString(undefined)}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-col gap-1">
                                {u.is_admin ? (
                                  <Badge 
                                    className="bg-blue-600 text-[10px] cursor-pointer hover:bg-blue-700 w-fit"
                                    onClick={() => toggleAdmin(u.id)}
                                    title="Click to toggle Admin"
                                  >
                                    Admin
                                  </Badge>
                                ) : (
                                  <Badge 
                                    variant="secondary" 
                                    className="text-[10px] cursor-pointer hover:bg-secondary/80 w-fit"
                                    onClick={() => toggleAdmin(u.id)}
                                    title="Click to toggle Admin"
                                  >
                                    User
                                  </Badge>
                                )}
                                {!u.is_confirmed && (
                                  <Badge 
                                    variant="destructive" 
                                    className="text-[10px] bg-yellow-600 hover:bg-yellow-700 cursor-pointer w-fit animate-pulse" 
                                    onClick={() => confirmUser(u.id)}
                                    title="Click to confirm user"
                                  >
                                    {language === 'de' ? 'Bestätigen' : 'Confirm'}
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400 hover:text-red-500 hover:bg-red-500/10" onClick={() => deleteUser(u.id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                        {data.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No users found.</TableCell>
                          </TableRow>
                        )}
                      </>
                    );
                  })()}
                </TableBody>
              </Table>
            </div>
            {(() => {
              const { totalPages } = getPaginatedData(users, 'users', u => 
                u.username.toLowerCase().includes(searchQuery.users.toLowerCase()) ||
                u.email.toLowerCase().includes(searchQuery.users.toLowerCase())
              );
              return renderPagination('users', totalPages);
            })()}
          </CardContent>
        </Card>

        {/* Wetten verwalten volle Breite */}
        <Card className="w-full">
          <CardHeader className="pb-2">
            <CardTitle>{language === 'de' ? 'Wetten verwalten' : 'Manage Markets'}</CardTitle>
          </CardHeader>
          <CardContent>
            {renderSearch('markets', 'Wetten durchsuchen...')}
            <div className="overflow-x-auto min-h-[450px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Market</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(() => {
                    const { data } = getPaginatedData(markets, 'markets', m => 
                      m.title.toLowerCase().includes(searchQuery.markets.toLowerCase()) ||
                      m.creator_username.toLowerCase().includes(searchQuery.markets.toLowerCase())
                    );
                    return (
                      <>
                        {data.map(m => (
                          <TableRow key={m.id} className={m.status === 'deleted' ? 'opacity-50 grayscale bg-red-500/5' : ''}>
                            <TableCell>
                              <div className="flex flex-col">
                                <span className="font-bold text-xs truncate max-w-[150px]" title={m.title}>{m.title}</span>
                                <span className="text-[10px] text-muted-foreground">@{m.creator_username}</span>
                                <Link to={`/market/${m.id}`} className="flex items-center gap-1 text-[10px] text-blue-400 hover:underline mt-1">
                                  <Eye className="w-3 h-3" />
                                  {language === 'de' ? 'Ansehen' : 'View'}
                                </Link>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-col gap-1">
                                <Badge 
                                  variant={m.status === 'open' ? 'outline' : 'secondary'}
                                  className={cn(
                                    m.status === 'resolved' && "bg-blue-500/10 text-blue-500 border-blue-500/20",
                                    m.status === 'deleted' && "bg-red-500/10 text-red-500 border-red-500/20",
                                    m.status === 'cancelled' && "bg-yellow-500/10 text-yellow-500 border-yellow-500/20"
                                  )}
                                >
                                  {m.status}
                                </Badge>
                                {m.status === 'resolved' && m.result && (
                                  <Badge className={cn("text-[10px] uppercase w-fit", m.result === 'yes' ? 'bg-green-600' : 'bg-red-600')}>
                                    Result: {m.result}
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                {m.status === 'open' && (
                                  <Button 
                                    variant="ghost" 
                                    size="icon" 
                                    className="text-primary hover:text-primary hover:bg-primary/10"
                                    title={language === 'de' ? 'Enddatum bearbeiten' : 'Edit End Date'}
                                    onClick={() => {
                                      let datePart = new Date().toISOString().split('T')[0];
                                      let timePart = "23:59";
                                      
                                      if (m.end_date) {
                                        const parts = m.end_date.split('T');
                                        datePart = parts[0];
                                        if (parts[1]) {
                                          timePart = parts[1].substring(0, 5);
                                        }
                                      }
                                      
                                      setEditingMarket({ id: m.id, title: m.title, end_date: datePart, end_time: timePart });
                                    }}
                                  >
                                    <Calendar className="w-4 h-4" />
                                  </Button>
                                )}
                                {m.status === 'open' && (
                                  <Button 
                                    variant="ghost" 
                                    size="icon" 
                                    className="text-green-500 hover:text-green-600 hover:bg-green-500/10"
                                    title={language === 'de' ? 'Ergebnis festlegen' : 'Resolve Market'}
                                    onClick={() => setResolveModal({ open: true, marketId: m.id, marketTitle: m.title })}
                                  >
                                    <CheckCircle2 className="w-4 h-4" />
                                  </Button>
                                )}
                                {m.status !== 'deleted' && (
                                  <Button variant="ghost" size="icon" className="text-red-400 hover:text-red-500 hover:bg-red-500/10" onClick={() => deleteMarket(m.id)}>
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                        {data.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={3} className="text-center py-8 text-muted-foreground">No markets found.</TableCell>
                          </TableRow>
                        )}
                      </>
                    );
                  })()}
                </TableBody>
              </Table>
            </div>
            {(() => {
              const { totalPages } = getPaginatedData(markets, 'markets', m => 
                m.title.toLowerCase().includes(searchQuery.markets.toLowerCase()) ||
                m.creator_username.toLowerCase().includes(searchQuery.markets.toLowerCase())
              );
              return renderPagination('markets', totalPages);
            })()}
          </CardContent>
        </Card>

        {/* Kategorie verwalten und Kommentar Moderation nebeneinander */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle>{language === 'de' ? 'Kategorien verwalten' : 'Manage Categories'}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto min-h-[450px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {categories.map(cat => (
                      <TableRow key={cat.id}>
                        <TableCell className="font-medium">{cat.name}</TableCell>
                        <TableCell>
                          <Button variant="ghost" size="icon" className="text-red-400" onClick={() => deleteCategory(cat.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {categories.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={2} className="text-center py-8 text-muted-foreground">No categories found.</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                {language === 'de' ? 'Kommentar Moderation' : 'Comment Moderation'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {renderSearch('comments', 'Nach Inhalt, User oder Wette suchen...')}
              <div className="overflow-x-auto min-h-[450px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Kommentar</TableHead>
                      <TableHead>User / Datum</TableHead>
                      <TableHead>Wette</TableHead>
                      <TableHead>Aktion</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(() => {
                      const { data } = getPaginatedData(comments, 'comments', c => 
                        c.comment.toLowerCase().includes(searchQuery.comments.toLowerCase()) ||
                        c.username.toLowerCase().includes(searchQuery.comments.toLowerCase()) ||
                        (c.market_title || '').toLowerCase().includes(searchQuery.comments.toLowerCase())
                      );
                      return (
                        <>
                          {data.map(c => (
                            <TableRow key={c.id}>
                              <TableCell className="max-w-[200px]">
                                <p className="text-sm leading-snug line-clamp-3" title={c.comment}>{c.comment}</p>
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-col">
                                  <span className="font-bold text-xs">@{c.username}</span>
                                  <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                                    {new Date(c.created_at).toLocaleString(undefined)}
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell className="max-w-[150px]">
                                <Link 
                                  to={`/market/${c.market_id}`} 
                                  className="text-blue-400 hover:underline text-[10px] font-medium line-clamp-2 leading-tight"
                                  title={c.market_title}
                                >
                                  {c.market_title}
                                </Link>
                              </TableCell>
                              <TableCell>
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="text-red-400 hover:text-red-500 hover:bg-red-500/10 h-8 w-8"
                                  onClick={() => deleteComment(c.id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                          {data.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">No comments found.</TableCell>
                            </TableRow>
                          )}
                        </>
                      );
                    })()}
                  </TableBody>
                </Table>
              </div>
              {(() => {
                const { totalPages } = getPaginatedData(comments, 'comments', c => 
                  c.comment.toLowerCase().includes(searchQuery.comments.toLowerCase()) ||
                  c.username.toLowerCase().includes(searchQuery.comments.toLowerCase()) ||
                  (c.market_title || '').toLowerCase().includes(searchQuery.comments.toLowerCase())
                );
                return renderPagination('comments', totalPages);
              })()}
            </CardContent>
          </Card>
        </div>

        {/* Ganz unten: die globale Transaktionshistory volle Breite */}
        <Card className="w-full">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-purple-500" />
              {language === 'de' ? 'Globale Transaktionshistorie' : 'Global Transaction History'}
            </CardTitle>
            <Badge variant="outline" className="font-mono">{transactions.length} Records</Badge>
          </CardHeader>
          <CardContent>
            {renderSearch('transactions', 'Nach Benutzer, Typ oder Beschreibung suchen...')}
            <div className="overflow-x-auto min-h-[450px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(() => {
                    const { data } = getPaginatedData(transactions, 'transactions', t => 
                      t.username.toLowerCase().includes(searchQuery.transactions.toLowerCase()) ||
                      t.type.toLowerCase().includes(searchQuery.transactions.toLowerCase()) ||
                      t.description.toLowerCase().includes(searchQuery.transactions.toLowerCase())
                    );
                    return (
                      <>
                        {data.map(t => (
                          <TableRow key={t.id}>
                            <TableCell className="font-bold">@{t.username}</TableCell>
                            <TableCell className="text-xs whitespace-nowrap">
                              {new Date(t.created_at).toLocaleString()}
                            </TableCell>
                            <TableCell>
                              <Badge 
                                variant="outline" 
                                className={cn(
                                  "text-[10px] uppercase",
                                  t.type === 'bet' && "border-red-500/50 text-red-500",
                                  t.type === 'payout' && "border-green-500/50 text-green-500",
                                  t.type === 'refund' && "border-blue-500/50 text-blue-500",
                                  t.type === 'initial' && "border-yellow-500/50 text-yellow-500",
                                  t.type === 'reversal' && "border-purple-500/50 text-purple-500"
                                )}
                              >
                                {t.type}
                              </Badge>
                            </TableCell>
                            <TableCell className={cn("font-mono font-bold", t.amount < 0 ? 'text-red-400' : 'text-green-400')}>
                              {t.amount > 0 ? '+' : ''}{t.amount.toLocaleString()} €
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground italic truncate max-w-[250px]" title={t.description}>
                              {t.description}
                            </TableCell>
                            <TableCell>
                              {t.type !== 'reversal' && (
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="h-8 w-8 text-orange-400 hover:text-orange-500 hover:bg-orange-500/10"
                                  onClick={() => reverseTransaction(t.id, t.description)}
                                  title={language === 'de' ? 'Transaktion stornieren' : 'Reverse Transaction'}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                        {data.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No transactions found.</TableCell>
                          </TableRow>
                        )}
                      </>
                    );
                  })()}
                </TableBody>
              </Table>
            </div>
            {(() => {
              const { totalPages } = getPaginatedData(transactions, 'transactions', t => 
                t.username.toLowerCase().includes(searchQuery.transactions.toLowerCase()) ||
                t.type.toLowerCase().includes(searchQuery.transactions.toLowerCase()) ||
                t.description.toLowerCase().includes(searchQuery.transactions.toLowerCase())
              );
              return renderPagination('transactions', totalPages);
            })()}
          </CardContent>
        </Card>
      </main>

      {/* User Edit Modal */}
      <Dialog open={!!editingUser} onOpenChange={(open) => !open && setEditingUser(null)}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Benutzer bearbeiten</DialogTitle>
          </DialogHeader>
          {editingUser && (
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="edit-username">Benutzername</Label>
                <Input 
                  id="edit-username" 
                  value={editingUser.username} 
                  onChange={(e) => setEditingUser({...editingUser, username: e.target.value})} 
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-email">E-Mail</Label>
                <Input 
                  id="edit-email" 
                  type="email"
                  value={editingUser.email} 
                  onChange={(e) => setEditingUser({...editingUser, email: e.target.value})} 
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-paypal">PayPal.me Link</Label>
                <Input 
                  id="edit-paypal" 
                  placeholder="https://paypal.me/..."
                  value={editingUser.paypal_link} 
                  onChange={(e) => setEditingUser({...editingUser, paypal_link: e.target.value})} 
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingUser(null)}>Abbrechen</Button>
            <Button onClick={handleUserUpdate}>Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Market End Date Edit Modal */}
      <Dialog open={!!editingMarket} onOpenChange={(open) => !open && setEditingMarket(null)}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Endzeitpunkt ändern</DialogTitle>
          </DialogHeader>
          {editingMarket && (
            <div className="grid gap-4 py-4">
              <p className="text-sm font-bold">{editingMarket.title}</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="edit-enddate">Datum</Label>
                  <Input 
                    id="edit-enddate" 
                    type="date"
                    value={editingMarket.end_date} 
                    onChange={(e) => setEditingMarket({...editingMarket, end_date: e.target.value})} 
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="edit-endtime">Uhrzeit</Label>
                  <Input 
                    id="edit-endtime" 
                    type="time"
                    value={editingMarket.end_time} 
                    onChange={(e) => setEditingMarket({...editingMarket, end_time: e.target.value})} 
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingMarket(null)}>Abbrechen</Button>
            <Button onClick={handleMarketEndDateUpdate}>Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <AlertDialog open={confirmModal.open} onOpenChange={(open) => setConfirmModal(prev => ({ ...prev, open }))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmModal.title}</AlertDialogTitle>
            <AlertDialogDescription>{confirmModal.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{language === 'de' ? 'Abbrechen' : 'Cancel'}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => {
                confirmModal.onConfirm();
                setConfirmModal(prev => ({ ...prev, open: false }));
              }}
              className={confirmModal.variant === 'destructive' ? "bg-red-600 hover:bg-red-700" : ""}
            >
              {language === 'de' ? 'Bestätigen' : 'Confirm'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Resolve Market Dialog */}
      <Dialog open={resolveModal.open} onOpenChange={(open) => setResolveModal(prev => ({ ...prev, open }))}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{language === 'de' ? 'Ergebnis festlegen' : 'Resolve Market'}</DialogTitle>
            <DialogDescription className="font-medium text-foreground">
              {resolveModal.marketTitle}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <Button 
              variant="outline" 
              className="h-24 flex flex-col gap-2 border-green-600/30 hover:bg-green-600/10 hover:border-green-600"
              onClick={() => resolveMarketAdmin(resolveModal.marketId, 'yes')}
              disabled={resolving}
            >
              <span className="text-2xl font-bold text-green-500">YES</span>
              <span className="text-xs text-muted-foreground">{language === 'de' ? 'Tritt ein' : 'It happens'}</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-24 flex flex-col gap-2 border-red-600/30 hover:bg-red-600/10 hover:border-red-600"
              onClick={() => resolveMarketAdmin(resolveModal.marketId, 'no')}
              disabled={resolving}
            >
              <span className="text-2xl font-bold text-red-500">NO</span>
              <span className="text-xs text-muted-foreground">{language === 'de' ? 'Tritt nicht ein' : "Doesn't happen"}</span>
            </Button>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setResolveModal({ open: false, marketId: null, marketTitle: '' })}>
              {language === 'de' ? 'Abbrechen' : 'Cancel'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
